'use client';

import {
    Check,
    CheckCircle,
    ChevronDown,
    ChevronUp,
    ExternalLink,
    FileText,
    MessageSquare,
    Phone,
    Play,
    Plus,
    Shield,
    Star,
    Wrench,
    Zap,
} from 'lucide-react';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useState } from 'react';
import PageHeader from '@/components/layout/PageHeader';
import AccessoryCard from '@/components/product/AccessoryCard';
import ComparisonTable from '@/components/product/ComparisonTable';
import RelatedProducts from '@/components/product/RelatedProducts';
import AnimateIn from '@/components/ui/AnimateIn';
import Button from '@/components/ui/Button';
import DeferredMount from '@/components/ui/DeferredMount';
import { MultiFormatImage } from '@/components/ui/MultiFormatImage';
import { useInquiry } from '@/contexts/InquiryContext';
import { trackEvent } from '@/lib/analytics';
import { buildPublicUrl } from '@/lib/r2-urls';

// Bottom-of-page CTA is below the fold and not needed for first paint —
// code-split it and defer mounting until it scrolls near the viewport.
const CTA = dynamic(() => import('@/components/ui/CTA'), { ssr: false });

const INITIAL_FEATURES_COUNT = 5;

export default function NoVariantsProduct({
    product,
    slug,
    prodSrcSet,
    prodBlurDataURL,
    bgSrcSet,
    bgBlurDataURL,
    breadcrumbs,
    accessories,
}) {
    const [isAdding, setIsAdding] = useState(false);
    const [showAllFeatures, setShowAllFeatures] = useState(false);
    const [videoSrc, setVideoSrc] = useState(() =>
        product?.embed ? buildPublicUrl(product.embed) : null,
    );
    const [videoFallbackApplied, setVideoFallbackApplied] = useState(false);
    const { addToInquiry, isInInquiry } = useInquiry();

    const handleAddToInquiry = async () => {
        setIsAdding(true);

        const inquiryItem = {
            type: 'product',
            name: product.name,
            image: product.img,
            description: product.description,
            specifications: product.specs
                ? product.specs.reduce((acc, spec) => {
                      acc[spec.label] = spec.value;
                      return acc;
                  }, {})
                : {},
        };

        addToInquiry(inquiryItem);
        trackEvent('add_to_inquiry', { product: product.name, slug });

        setTimeout(() => {
            setIsAdding(false);
        }, 1000);
    };

    const isInList = isInInquiry({
        type: 'product',
        name: product.name,
    });

    const localVideoPath = product?.embed
        ? product.embed.startsWith('/')
            ? product.embed
            : `/${product.embed}`
        : null;

    useEffect(() => {
        const initialSrc = product?.embed
            ? buildPublicUrl(product.embed)
            : null;
        setVideoSrc(initialSrc);
        setVideoFallbackApplied(false);
    }, [product?.embed]);

    const handleVideoError = useCallback(() => {
        if (!localVideoPath) return;
        if (!videoFallbackApplied) {
            setVideoSrc(localVideoPath);
            setVideoFallbackApplied(true);
        }
    }, [localVideoPath, videoFallbackApplied]);

    // Determine which features to show
    const hasMoreFeatures =
        product.features && product.features.length > INITIAL_FEATURES_COUNT;
    const visibleFeatures = showAllFeatures
        ? product.features
        : product.features?.slice(0, INITIAL_FEATURES_COUNT);

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Header */}
            <PageHeader
                title={product.name}
                description={product.description}
                backgroundImage={product.bannerimg}
                bgSrcSet={bgSrcSet}
                bgBlurDataURL={bgBlurDataURL}
                breadcrumbs={
                    breadcrumbs || [
                        { label: 'Home', href: '/' },
                        { label: 'Products', href: '/products' },
                        { label: product.name },
                    ]
                }
                backLabel="Go Back"
                prodSrcSet={prodSrcSet}
                prodBlurDataURL={prodBlurDataURL}
                primaryAction={{
                    label: 'Contact Sales',
                    onClick: () => {
                        window.location.href = '/contact';
                    },
                    icon: <Phone className="w-4 h-4" />,
                }}
                secondaryAction={{
                    label: isAdding
                        ? 'Adding...'
                        : isInList
                          ? 'Added to Inquiry'
                          : 'Add to Inquiry',
                    onClick: handleAddToInquiry,
                    icon: isAdding ? (
                        <div className="w-4 h-4 border-2 border-brand border-t-transparent rounded-full animate-spin"></div>
                    ) : isInList ? (
                        <Check className="w-4 h-4" />
                    ) : (
                        <Plus className="w-4 h-4" />
                    ),
                }}
            />

            <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Main Content Grid — 2-col: image left, details right */}
                <AnimateIn>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                        {/* Left Column: Product Image */}
                        <div className="space-y-6">
                            {/* Product Image */}
                            <section className="bg-white shadow-sm border border-gray-100 overflow-hidden">
                                <div className="aspect-[4/3] relative bg-linear-to-br from-gray-50 to-gray-100 flex items-center justify-center p-6">
                                    <MultiFormatImage
                                        src={
                                            product.img ||
                                            '/placeholder.svg?height=500&width=500&query=product'
                                        }
                                        alt={product.name}
                                        width={600}
                                        height={450}
                                        className="w-full h-full object-contain"
                                        sizes="(max-width: 1024px) 100vw, 50vw"
                                        priority
                                    />
                                </div>
                            </section>

                            {/* About Section */}
                            <section className="bg-white shadow-sm border border-gray-100 overflow-hidden">
                                <div className="px-6 py-4 border-b border-gray-100">
                                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                        <FileText className="w-5 h-5 text-brand" />
                                        About {product.name}
                                    </h2>
                                </div>
                                <div className="p-6">
                                    <p className="text-gray-700 leading-relaxed text-base">
                                        {product.longDesc ||
                                            product.description}
                                    </p>
                                </div>
                            </section>
                        </div>

                        {/* Right Column: Features + Specs + CTA */}
                        <div className="space-y-6">
                            {/* Key Features - Collapsible */}
                            {product.features &&
                                product.features.length > 0 && (
                                    <section className="bg-white shadow-sm border border-gray-100 overflow-hidden">
                                        <div className="bg-linear-to-r from-brand to-brand-dark px-6 py-4">
                                            <h2 className="text-white font-semibold flex items-center gap-2">
                                                <Star className="w-5 h-5" />
                                                Key Features
                                            </h2>
                                        </div>
                                        <div className="p-4">
                                            <ul className="space-y-2">
                                                {visibleFeatures.map(
                                                    (feature, index) => (
                                                        <li
                                                            key={index}
                                                            className="flex items-start gap-3 p-2 hover:bg-gray-50 hover:cursor-pointer transition-colors"
                                                        >
                                                            <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                                                            <span className="text-gray-700 text-sm leading-relaxed">
                                                                {feature}
                                                            </span>
                                                        </li>
                                                    ),
                                                )}
                                            </ul>
                                            {hasMoreFeatures && (
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setShowAllFeatures(
                                                            !showAllFeatures,
                                                        )
                                                    }
                                                    className="mt-3 w-full flex items-center justify-center gap-2 text-brand hover:text-brand hover:cursor-pointer font-medium text-sm py-2 hover:bg-brand/5 transition-colors"
                                                >
                                                    {showAllFeatures ? (
                                                        <>
                                                            Show Less{' '}
                                                            <ChevronUp className="w-4 h-4" />
                                                        </>
                                                    ) : (
                                                        <>
                                                            Show{' '}
                                                            {product.features
                                                                .length -
                                                                INITIAL_FEATURES_COUNT}{' '}
                                                            More{' '}
                                                            <ChevronDown className="w-4 h-4" />
                                                        </>
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </section>
                                )}

                            {/* Technical Specs - Compact */}
                            {product.specs && product.specs.length > 0 && (
                                <section className="bg-white shadow-sm border border-gray-100 overflow-hidden">
                                    <div className="px-6 py-4 border-b border-gray-100">
                                        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                                            <Wrench className="w-5 h-5 text-brand" />
                                            Specifications
                                        </h2>
                                    </div>
                                    <div className="divide-y divide-gray-100">
                                        {product.specs.map((spec, index) => (
                                            <div
                                                key={index}
                                                className="px-4 py-3 flex justify-between items-center hover:bg-gray-50 transition-colors"
                                            >
                                                <span className="text-gray-600 text-sm">
                                                    {spec.label}
                                                </span>
                                                <span className="text-gray-900 font-medium text-sm text-right">
                                                    {Array.isArray(spec.value)
                                                        ? spec.value.join(', ')
                                                        : spec.value}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* CTA Card - Sticky on scroll for desktop */}
                            <section className="bg-linear-to-br from-gray-900 to-gray-800 p-6 text-white">
                                <h3 className="text-lg font-bold mb-2">
                                    Ready to get started?
                                </h3>
                                <p className="text-gray-300 text-sm mb-4">
                                    Talk to our team about your project
                                    requirements and get a quote.
                                </p>
                                <div className="space-y-3">
                                    <Button
                                        href="/contact"
                                        variant="primary"
                                        size="default"
                                        className="w-full gap-2"
                                    >
                                        <Phone className="w-5 h-5" />
                                        Contact Sales
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={handleAddToInquiry}
                                        disabled={isInList}
                                        variant="ghost-inverted"
                                        size="default"
                                        className="w-full gap-2"
                                    >
                                        {isInList ? (
                                            <>
                                                <Check className="w-5 h-5" />
                                                Added to Inquiry
                                            </>
                                        ) : (
                                            <>
                                                <Plus className="w-5 h-5" />
                                                Add to Inquiry List
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </section>
                        </div>
                    </div>
                </AnimateIn>

                {/* Video Section - full width below grid */}
                {videoSrc && (
                    <AnimateIn>
                        <section className="mb-8 bg-white shadow-sm border border-gray-100 overflow-hidden">
                            <div className="bg-gray-900 px-4 py-3 flex items-center gap-2">
                                <Play className="w-5 h-5 text-brand" />
                                <h2 className="text-white font-semibold">
                                    See It In Action
                                </h2>
                            </div>
                            <div
                                className="relative"
                                style={{ paddingBottom: '56.25%' }}
                            >
                                <video
                                    src={videoSrc}
                                    className="absolute inset-0 w-full h-full object-cover bg-black"
                                    controls
                                    playsInline
                                    autoPlay={false}
                                    muted={false}
                                    preload="metadata"
                                    title={product.name}
                                    crossOrigin="anonymous"
                                    onError={handleVideoError}
                                >
                                    <source src={videoSrc} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            </div>
                        </section>
                    </AnimateIn>
                )}

                {/* Comparison Table - Full Width, Prominent */}
                {product.comparisonData && (
                    <ComparisonTable
                        comparisonData={product.comparisonData}
                        productName={product.name}
                    />
                )}

                {/* Trust Badges */}
                <AnimateIn>
                    <section className="mb-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-white p-5 shadow-sm border border-gray-100 flex items-center gap-4">
                                <div className="bg-brand/10 w-12 h-12 rounded-lg flex items-center justify-center shrink-0">
                                    <Shield className="w-6 h-6 text-brand" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900">
                                        Safety Certified
                                    </h3>
                                    <p className="text-gray-500 text-sm">
                                        Meets NZ safety standards
                                    </p>
                                </div>
                            </div>
                            <div className="bg-white p-5 shadow-sm border border-gray-100 flex items-center gap-4">
                                <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center shrink-0">
                                    <Zap className="w-6 h-6 text-blue-600" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900">
                                        Quick Delivery
                                    </h3>
                                    <p className="text-gray-500 text-sm">
                                        Fast turnaround NZ-wide
                                    </p>
                                </div>
                            </div>
                            <div className="bg-white p-5 shadow-sm border border-gray-100 flex items-center gap-4">
                                <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center shrink-0">
                                    <CheckCircle className="w-6 h-6 text-green-600" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900">
                                        Expert Support
                                    </h3>
                                    <p className="text-gray-500 text-sm">
                                        Technical advice included
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>
                </AnimateIn>

                {/* Compatible Accessories */}
                {accessories && accessories.length > 0 && (
                    <AnimateIn>
                        <section className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-3">
                                Compatible Accessories
                            </h2>
                            <p className="text-gray-600 mb-6">
                                Enhance your {product.name}&rsquo;s
                                functionality with these compatible accessories.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                                {accessories.map((accessory, index) => (
                                    <AccessoryCard
                                        key={accessory.id ?? index}
                                        accessory={accessory}
                                    />
                                ))}
                            </div>
                        </section>
                    </AnimateIn>
                )}

                {/* Related Products */}
                <RelatedProducts relatedNames={product.related} />

                {/* External Website Link (if available) */}
                {product.website && (
                    <section className="mb-8">
                        <a
                            href={product.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 w-full bg-white hover:bg-gray-50 text-gray-700 px-6 py-4 border border-gray-200 transition-colors font-medium"
                        >
                            <ExternalLink className="w-5 h-5" />
                            Visit Manufacturer Website
                        </a>
                    </section>
                )}
            </main>

            <DeferredMount>
                <CTA
                    heading="Need Help Choosing the Right Equipment?"
                    subtext="Our technical team can help you select the right product for your specific project requirements."
                    primaryButton={{
                        href: '/contact',
                        label: 'Get Expert Advice',
                        icon: <Phone className="w-5 h-5 mr-2" />,
                        trackingLabel: 'Get Expert Advice',
                    }}
                    secondaryButton={{
                        href: '/resources/faq',
                        label: 'FAQ',
                        icon: <MessageSquare className="w-5 h-5 mr-2" />,
                        trackingLabel: 'FAQ',
                    }}
                    ctaSection="product_no_variants_cta"
                />
            </DeferredMount>
        </div>
    );
}
